import React, { useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "./CreateInvoice.css";

function CreateInvoice() {
  const invoiceRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const [formData, setFormData] = useState({
    customerName: "",
    country: "",
    shipVia: "RORO",

    invoiceNumber: "",
    invoiceDate: "",
    dueDate: "",
    portOfLoading: "Japan",
    portOfDischarge: "",

    stockNumber: "",
    carName: "",
    make: "",
    model: "",
    year: "",
    color: "",

    mileage: "",
    engine: "",
    fuel: "",
    transmission: "",
    steering: "",
    drive: "",
    doors: "",
    seats: "",

    quantity: 1,
    unitPrice: "",
  });

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previousData) => ({
      ...previousData,
      [name]: value,
    }));
  };

  const quantity = Number(formData.quantity) || 0;
  const unitPrice = Number(formData.unitPrice) || 0;
  const total = quantity * unitPrice;

  const formatCurrency = (amount) => {
    return Number(amount || 0).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  };

  const formatDate = (date) => {
    if (!date) return "";

    return new Date(`${date}T00:00:00`).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const downloadInvoice = async () => {
    if (!invoiceRef.current) return;

    try {
      setIsDownloading(true);

      // Wait for fonts to finish loading.
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }

      // Wait for the logo and stamp images before creating the PDF.
      const images = invoiceRef.current.querySelectorAll("img");

      await Promise.all(
        Array.from(images).map(
          (image) =>
            new Promise((resolve) => {
              if (image.complete) {
                resolve();
                return;
              }

              image.onload = resolve;
              image.onerror = resolve;
            })
        )
      );

      // Each .invoice-document becomes exactly one PDF page.
      const pages = Array.from(
        invoiceRef.current.querySelectorAll(".invoice-document")
      );

      if (pages.length === 0) {
        throw new Error("No invoice pages were found.");
      }

      const pageWidth = 794;
      const pageHeight = 1123;

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: [pageWidth, pageHeight],
        hotfixes: ["px_scaling"],
      });

      for (let index = 0; index < pages.length; index += 1) {
        const page = pages[index];

        const canvas = await html2canvas(page, {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          backgroundColor: "#ffffff",
          logging: false,
          scrollX: 0,
          scrollY: -window.scrollY,
          width: pageWidth,
          height: pageHeight,
          windowWidth: pageWidth,
          windowHeight: pageHeight,
        });

        const imageData = canvas.toDataURL("image/jpeg", 0.98);

        if (index > 0) {
          pdf.addPage([pageWidth, pageHeight], "portrait");
        }

        pdf.addImage(
          imageData,
          "JPEG",
          0,
          0,
          pageWidth,
          pageHeight,
          undefined,
          "FAST"
        );
      }

      const safeInvoiceNumber = (
        formData.invoiceNumber || "Shipment-Solutions-Invoice"
      )
        .trim()
        .replace(/[<>:"/\\|?*]+/g, "-");

      pdf.save(`${safeInvoiceNumber}.pdf`);
    } catch (error) {
      console.error("Invoice download error:", error);
      alert("Invoice could not be downloaded. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="invoice-generator-page">
      <div className="invoice-generator-header">
        <div>
          <h1>Create Invoice</h1>
          <p>Enter the customer and vehicle details below.</p>
        </div>

        <button
          type="button"
          className="invoice-download-button"
          onClick={downloadInvoice}
          disabled={isDownloading}
        >
          {isDownloading ? "Generating PDF..." : "Download Invoice"}
        </button>
      </div>

      <div className="invoice-generator-layout">
        {/* Form */}
        <div className="invoice-form-container">
          <InvoiceSection title="Customer Information">
            <FormInput
              label="Customer Name"
              name="customerName"
              value={formData.customerName}
              onChange={handleChange}
            />

            <FormInput
              label="Country"
              name="country"
              value={formData.country}
              onChange={handleChange}
            />

            <FormInput
              label="Ship Via"
              name="shipVia"
              value={formData.shipVia}
              onChange={handleChange}
            />
          </InvoiceSection>

          <InvoiceSection title="Invoice Information">
            <FormInput
              label="Invoice Number"
              name="invoiceNumber"
              value={formData.invoiceNumber}
              onChange={handleChange}
              placeholder="INV-2001"
            />

            <FormInput
              label="Invoice Date"
              name="invoiceDate"
              type="date"
              value={formData.invoiceDate}
              onChange={handleChange}
            />

            <FormInput
              label="Payment Due Date"
              name="dueDate"
              type="date"
              value={formData.dueDate}
              onChange={handleChange}
            />

            <FormInput
              label="Port of Loading"
              name="portOfLoading"
              value={formData.portOfLoading}
              onChange={handleChange}
            />

            <FormInput
              label="Port of Discharge"
              name="portOfDischarge"
              value={formData.portOfDischarge}
              onChange={handleChange}
            />
          </InvoiceSection>

          <InvoiceSection title="Vehicle Information">
            <FormInput
              label="Stock Number"
              name="stockNumber"
              value={formData.stockNumber}
              onChange={handleChange}
            />

            <FormInput
              label="Car Name"
              name="carName"
              value={formData.carName}
              onChange={handleChange}
              placeholder="MAZDA CX5 XD PROACTIVE"
            />

            <FormInput
              label="Make"
              name="make"
              value={formData.make}
              onChange={handleChange}
            />

            <FormInput
              label="Model"
              name="model"
              value={formData.model}
              onChange={handleChange}
            />

            <FormInput
              label="Year"
              name="year"
              value={formData.year}
              onChange={handleChange}
            />

            <FormInput
              label="Color"
              name="color"
              value={formData.color}
              onChange={handleChange}
            />

            <FormInput
              label="Mileage"
              name="mileage"
              value={formData.mileage}
              onChange={handleChange}
              placeholder="103,000 km"
            />

            <FormInput
              label="Engine"
              name="engine"
              value={formData.engine}
              onChange={handleChange}
              placeholder="2200cc"
            />

            <FormInput
              label="Fuel"
              name="fuel"
              value={formData.fuel}
              onChange={handleChange}
            />

            <FormInput
              label="Transmission"
              name="transmission"
              value={formData.transmission}
              onChange={handleChange}
            />

            <FormInput
              label="Steering"
              name="steering"
              value={formData.steering}
              onChange={handleChange}
              placeholder="RHD"
            />

            <FormInput
              label="Drive"
              name="drive"
              value={formData.drive}
              onChange={handleChange}
              placeholder="4WD"
            />

            <FormInput
              label="Doors"
              name="doors"
              type="number"
              value={formData.doors}
              onChange={handleChange}
            />

            <FormInput
              label="Seats"
              name="seats"
              type="number"
              value={formData.seats}
              onChange={handleChange}
            />
          </InvoiceSection>

          <InvoiceSection title="Payment Information">
            <FormInput
              label="Quantity"
              name="quantity"
              type="number"
              min="1"
              value={formData.quantity}
              onChange={handleChange}
            />

            <FormInput
              label="Unit Price (USD)"
              name="unitPrice"
              type="number"
              min="0"
              value={formData.unitPrice}
              onChange={handleChange}
            />

            <div className="calculated-total">
              <span>Total</span>
              <strong>{formatCurrency(total)}</strong>
            </div>
          </InvoiceSection>
        </div>

        {/* Preview */}
        <div className="invoice-preview-wrapper">
          <h2>Invoice Preview</h2>

          <div ref={invoiceRef} className="invoice-pdf-content">
            <div className="invoice-document">
              <div className="invoice-company-header">
                <img
                  src="/invoice/shipment-logo.png"
                  alt="Shipment Solutions Logo"
                  className="invoice-company-logo"
                />

                <div className="invoice-company-details">
                  <strong>Shipment Solutions LLC</strong>
                  <span>Houston, Texas, USA</span>
                  <span>
                    Phone: +1 (346) 464-9730 | Email:
                    info@shipmentsolutionsllc.com
                  </span>
                </div>
              </div>

              <h1 className="invoice-document-title">
                PROFORMA INVOICE
              </h1>

              <table className="invoice-details-table">
                <tbody>
                  <tr>
                    <td>
                      <strong>Customer Name:</strong>{" "}
                      {formData.customerName || "—"}
                      <br />

                      <strong>Country:</strong>{" "}
                      {formData.country || "—"}
                      <br />

                      <strong>Ship Via:</strong>{" "}
                      {formData.shipVia || "—"}
                    </td>

                    <td>
                      <strong>Invoice No:</strong>{" "}
                      {formData.invoiceNumber || "—"}
                      <br />

                      <strong>Date:</strong>{" "}
                      {formatDate(formData.invoiceDate) || "—"}
                      <br />

                      <strong>Port of Loading:</strong>{" "}
                      {formData.portOfLoading || "—"}
                      <br />

                      <strong>Port of Discharge:</strong>{" "}
                      {formData.portOfDischarge || "—"}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className="invoice-important-notice">
                IMPORTANT NOTICE: Please mention Invoice No, Remitter
                Name & Customer Name on the TT slip
              </div>

              <div className="invoice-bank-details">
                <strong>BANK INFORMATION</strong>
                <br />
                <b>Bank Name:</b> Chase Bank
                <br />
                <b>Bank Address:</b> JPMorgan Chase Bank, N.A. PO Box
                182051 Columbus, OH 43218-2051
                <br />
                <b>Account Name:</b> Shipment Solutions LLC
                <br />
                <b>Account No:</b> 539839897
                <br />
                <b>Swift Code:</b>
                <br />
                <b>Routing Number:</b> 111000614
              </div>

              <table className="invoice-car-table">
                <thead>
                  <tr>
                    <th>STOCK NO</th>
                    <th>CAR DETAILS</th>
                    <th>ADDITIONAL INFO</th>
                    <th>QUANTITY</th>
                    <th>UNIT PRICE</th>
                    <th>TOTAL</th>
                  </tr>
                </thead>

                <tbody>
                  <tr>
                    <td>{formData.stockNumber || "—"}</td>

                    <td>
                      <strong>Name:</strong>{" "}
                      {formData.carName || "—"}
                      <br />

                      <strong>Make:</strong>{" "}
                      {formData.make || "—"}
                      <br />

                      <strong>Model:</strong>{" "}
                      {formData.model || "—"}
                      <br />

                      <strong>Year:</strong>{" "}
                      {formData.year || "—"}
                      <br />

                      <strong>Color:</strong>{" "}
                      {formData.color || "—"}
                    </td>

                    <td>
                      <strong>Mileage:</strong>{" "}
                      {formData.mileage || "—"}
                      <br />

                      <strong>Engine:</strong>{" "}
                      {formData.engine || "—"}
                      <br />

                      <strong>Fuel:</strong>{" "}
                      {formData.fuel || "—"}
                      <br />

                      <strong>Transmission:</strong>{" "}
                      {formData.transmission || "—"}
                      <br />

                      <strong>Steering:</strong>{" "}
                      {formData.steering || "—"}
                      <br />

                      <strong>Drive:</strong>{" "}
                      {formData.drive || "—"}
                      <br />

                      <strong>Doors:</strong>{" "}
                      {formData.doors || "—"}
                      <br />

                      <strong>Seats:</strong>{" "}
                      {formData.seats || "—"}
                    </td>

                    <td>{quantity}</td>
                    <td>{formatCurrency(unitPrice)}</td>
                    <td>{formatCurrency(total)}</td>
                  </tr>
                </tbody>
              </table>

              <div className="invoice-total-area">
                <p>
                  <strong>Total: {formatCurrency(total)}</strong>
                </p>

                <p>
                  Payment Due Date:{" "}
                  <strong>
                    {formatDate(formData.dueDate) || "—"}
                  </strong>
                </p>
              </div>

              <div className="invoice-signature-area">
                <strong>Authorized Signature:</strong>

                <img
                  src="/invoice/stamp.png"
                  alt="Company Stamp"
                  className="invoice-company-stamp"
                />

                <div className="invoice-signature-line">
                  ___________________________
                  <br />
                  Shipment Solutions LLC
                </div>
              </div>
            </div>   

            <div className="invoice-document invoice-terms-page">
              <h2>Terms & Conditions – Shipment Solutions LLC</h2>

              <TermsContent />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InvoiceSection({ title, children }) {
  return (
    <section className="invoice-form-section">
      <h2>{title}</h2>
      <div className="invoice-form-grid">{children}</div>
    </section>
  );
}

function FormInput({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder = "",
  min,
}) {
  return (
    <div className="invoice-form-group">
      <label htmlFor={name}>{label}</label>

      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        min={min}
      />
    </div>
  );
}

function TermsContent() {
  return (
    <div className="invoice-terms-content">
      <p>
        <strong>General:</strong> Shipment Solutions LLC (“Shipment Solutions”)
        conducts all transactions in accordance with these Terms & Conditions
        (“Terms”), unless otherwise agreed in a written contract signed by both
        parties. In case of any conflict between these Terms and a separate
        written contract, the latter shall prevail.
      </p>

      <p>
        <strong>Language:</strong> Only the English version of these Terms is
        legally binding. Any translations are provided solely for convenience
        and hold no legal effect.
      </p>

      <p>
        <strong>Pricing & Sale:</strong> Vehicles are priced in USD unless
        otherwise specified. Ownership remains with Shipment Solutions until
        full payment is received. Shipment Solutions reserves the right to
        retain a security interest in the vehicle(s) to cover any outstanding
        payments, logistics charges, or regulatory fees.
      </p>

      <p>
        <strong>Payment:</strong> All payments must be made by the due date
        mentioned on the invoice. Payments are considered received only upon
        confirmation of electronic funds transfer by Shipment Solutions. Any
        applicable bank charges are the responsibility of the buyer and are not
        included in the invoice total. Refunds, if any, will only be issued to
        the originating bank account.
      </p>

      <p>
        <strong>Shipping & Delivery:</strong> Shipment Solutions will arrange
        shipping upon full or partial payment confirmation. Delivery is
        considered complete once the vehicle is handed over to the shipping
        carrier. Risk transfers to the buyer at this point. Shipment schedules
        and timelines are governed by third-party shipping companies, and
        Shipment Solutions is not liable for delays, errors, or cancellations
        caused by carriers. Buyers are responsible for ensuring compliance with
        their country’s import regulations (e.g. vehicle age limits, emission
        standards, or steering side laws).
      </p>

      <p>
        <strong>Documents:</strong> Original shipping and export documents will
        only be released after full payment is received. Shipment Solutions is
        not responsible for losses arising from delays, damage, or mishandling
        of documents during courier transit.
      </p>

      <p>
        <strong>Non-Payment & Order Cancellation:</strong> If payment is not
        received in full by the due date, the vehicle will be considered unpaid,
        and Shipment Solutions reserves the right to resell the vehicle without
        notice. Orders become non-cancellable once shipping is booked. Any
        partial payments made will be forfeited in case of default or
        cancellation by the buyer.
      </p>

      <p>
        <strong>Liability Limitation:</strong> Shipment Solutions’ liability is
        strictly limited to the value of the vehicle stated on the invoice. We
        are not liable for any indirect, incidental, or consequential damages,
        including loss of profit or business interruption.
      </p>

      <p>
        <strong>Third-Party Service Providers:</strong> Shipment Solutions is
        not liable for any delays or errors caused by external service providers
        including but not limited to banks, shipping lines, customs authorities,
        or couriers.
      </p>

      <p>
        <strong>Vehicle Condition & Warranty:</strong> All vehicles are sold
        as-is, with no warranties expressed or implied except where explicitly
        stated. Buyers are encouraged to review all photos, descriptions, and
        specifications before purchase. Claims regarding undisclosed defects
        must be made within 7 days of vehicle arrival at the destination port,
        otherwise, they are deemed waived.
      </p>

      <p>
        <strong>Insurance:</strong> Buyers may optionally purchase marine cargo
        insurance through Shipment Solutions. Details will be provided upon
        request. Shipment Solutions is not responsible for any damage or loss
        during transit unless insurance has been purchased.
      </p>

      <p>
        <strong>Anti-Corruption Policy:</strong> Shipment Solutions adheres to
        all applicable international anti-corruption laws. Bribery or unlawful
        facilitation payments are strictly prohibited in all transactions.
      </p>

      <p>
        <strong>Compliance with Laws:</strong> Shipment Solutions complies with
        export control regulations, including restrictions on sanctioned
        countries, entities, and goods. Transactions violating such laws will be
        void and subject to cancellation.
      </p>

      <p>
        <strong>Inactive Accounts:</strong> If a customer remains inactive for
        more than 4 years, any remaining balance in their Shipment Solutions
        account may be forfeited unless a written request for refund is
        submitted and verified.
      </p>

      <p>
        <strong>Governing Law & Jurisdiction:</strong> These Terms are governed
        by and construed in accordance with the laws of the United Arab Emirates
        (or your operating jurisdiction). Shipment Solutions reserves the right
        to initiate legal proceedings in any appropriate jurisdiction. By
        transacting with us, the customer agrees to submit to the jurisdiction
        of such courts.
      </p>

      <p>
        <strong>Miscellaneous:</strong> Failure to enforce any provision of these
        Terms shall not constitute a waiver of such right. If any section of
        these Terms is found to be unenforceable, the remaining sections shall
        remain in full effect.
      </p>
    </div>
  );
}

export default CreateInvoice;